import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';

import { useFieldArray, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import isEqualWith from 'lodash.isequalwith';

import { ConfirmationModal, ConfirmationModalType } from '@wf/components';

import EmptyContentView from '../../../../components/ListWithContentLayout/components/EmptyContentView';
import ListWithContentLayout from '../../../../components/ListWithContentLayout';
import AttachmentBulkUploadModal from './AttachmentBulkUploadModal';
import UploadAttachmentsButton from './UploadAttachmentsButton';
import AttachNewDocumentButton from './AttachNewDocumentButton';
import { useLicenseContext } from '../../LicenseModeContext';
import AttachmentList from './AttachmentList';
import AttachmentForm from './AttachmentForm';

import { type IAttachmentFormValues, type IAttachmentStateItem } from './types';
import { type IAttachedDocumentConfig } from '../../types/response';
import useUpdateEffect from '../../../../hooks/useUpdateEffect';
import { ArrayFields } from '../../types/fieldEnums';

import {
  attachmentPermissionsSelector,
  attachmentsSelector,
  defaultAttachmentsSelector,
  mandatoryAttachmentCodesFromErrorSelector,
  parentLpcoAttachmentsSelector,
} from '../../store/selectors';
import { getAttachmentValues, getAttachmentValuesFromConfigs } from '../../helpers/getFormValues';
import { RimmEntityType } from '../../../rimm/data/rimmEntityEnums';
import { fetchRimmEntityByCodes } from '../../../rimm/services';
import { removeArrayFieldError } from '../../store/slice';
import { toasterSuccess } from '../../../../helpers';

interface IAttachmentsProps {
  savedAttachments: IAttachmentStateItem[];
  setSavedAttachments: Dispatch<SetStateAction<IAttachmentStateItem[]>>;
}

function Attachments({ savedAttachments, setSavedAttachments }: IAttachmentsProps) {
  const { t } = useTranslation();
  const { createMode, viewMode } = useLicenseContext();

  const dispatch = useDispatch();

  const defaultAttachments = useSelector(defaultAttachmentsSelector);
  const mandatoryAttachmentCodesFromError = useSelector(mandatoryAttachmentCodesFromErrorSelector);

  const parentLpcoAttachments = useSelector(parentLpcoAttachmentsSelector);
  const parentLpcoAttachmentValues = useMemo(
    () =>
      parentLpcoAttachments?.map((attachment) => ({
        ...getAttachmentValues(attachment),
        fileName: attachment.fileUrl,
      })) ?? [],
    [parentLpcoAttachments]
  ) as IAttachmentFormValues[];
  const permissions = useSelector(attachmentPermissionsSelector);

  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showImportFromParentModal, setShowImportFromParentModal] = useState(false);
  const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState<Nullable<number>>(null);
  const [nextSelectedAttachmentIndex, setNextSelectedAttachmentIndex] = useState<Nullable<number>>(null);
  const formType = selectedAttachmentIndex === savedAttachments.length ? 'create' : 'edit';

  const initialAttachments = useSelector(attachmentsSelector);

  const {
    control,
    getValues,
    trigger,
    formState: { isSubmitted },
  } = useFormContext();

  const { append, remove, update } = useFieldArray({
    name: ArrayFields.attachedDocuments,
    control,
    shouldUnregister: false,
  });

  useUpdateEffect(() => {
    if (initialAttachments) {
      const savedAttachments = initialAttachments
        .map(getAttachmentValues)
        .map((attachment) => ({ ...attachment, ...(!attachment.id && { id: crypto.randomUUID() }) }));

      setSavedAttachments(savedAttachments as IAttachmentStateItem[]);
    }
  }, [initialAttachments]);

  useUpdateEffect(() => {
    if (mandatoryAttachmentCodesFromError.length) {
      const getAttachedDocumentList = async () => {
        let mandatoryAttachmentData = mandatoryAttachmentCodesFromError.map(
          (code) => ({ code, description: '' } as IAttachedDocumentConfig)
        );

        try {
          const {
            data: { resultList },
          } = await fetchRimmEntityByCodes({
            entity: RimmEntityType.attachedDocument,
            codes: mandatoryAttachmentCodesFromError,
          });
          mandatoryAttachmentData = resultList as IAttachedDocumentConfig[];
        } catch (e) {
          console.error(e);
        } finally {
          const savedValues = getAttachmentValuesFromConfigs(mandatoryAttachmentData, true);
          const formValues = getAttachmentValuesFromConfigs(mandatoryAttachmentData);
          setSavedAttachments((prev) => [...prev, ...(savedValues as IAttachmentStateItem[])]);
          append(formValues);
        }
      };

      void getAttachedDocumentList();
    }
  }, [mandatoryAttachmentCodesFromError]);

  useUpdateEffect(() => {
    if (createMode && !initialAttachments) {
      setSavedAttachments(
        defaultAttachments?.length
          ? (getAttachmentValuesFromConfigs(defaultAttachments, true) as IAttachmentStateItem[])
          : []
      );
    }

    setSelectedAttachmentIndex(null);
  }, [defaultAttachments]);

  const getSelectedAttachmentValuesAreChanged = () => {
    if (formType === 'create') {
      return true;
    }

    const { id: savedAttachmentId, ...currentAttachmentSavedValues } = savedAttachments[
      selectedAttachmentIndex as number
    ] as IAttachmentStateItem;
    const { id: editedAttachmentId, ...currentAttachmentFieldValues } = getValues(
      `${ArrayFields.attachedDocuments}.${selectedAttachmentIndex as number}`
    ) as IAttachmentFormValues;

    return !isEqualWith(currentAttachmentFieldValues, currentAttachmentSavedValues, (a, b) => {
      // need to treat empty strings and nulls as equal when comparing values
      if ((a === null || a === '') && (b === null || b === '')) {
        return true;
      }
    });
  };

  const openCreateForm = () => {
    const index = savedAttachments.length;
    append({
      code: null,
      referenceNumber: '',
      attachmentDate: null,
      fileUrl: '',
    });
    setSelectedAttachmentIndex(index);
  };

  const handleDeleteAttachment = () => {
    setSavedAttachments(savedAttachments.filter((_, index) => index !== selectedAttachmentIndex));
    remove(selectedAttachmentIndex as number);
    setSelectedAttachmentIndex(null);
    setShowDeletePopup(false);
    dispatch(
      removeArrayFieldError({ entity: ArrayFields.attachedDocuments, index: selectedAttachmentIndex as number })
    );
  };

  const handleCancel = () => {
    const index = selectedAttachmentIndex as number;

    if (formType === 'create') {
      remove(index);
    } else if (formType === 'edit') {
      const { id, ...attachment } = savedAttachments[index] as IAttachmentStateItem;

      update(index, {
        ...attachment,
        ...(typeof id === 'number' && { id }), // add ids to form only for already saved attachments
      });
      void (isSubmitted && trigger(`${ArrayFields.attachedDocuments}.${index}`));
    }

    setSelectedAttachmentIndex(null);
  };

  const handleUploadAttachments = (uploadedAttachments: IAttachmentFormValues[]) => {
    const attCodes = uploadedAttachments.map((att) => att?.code?.value).filter(Boolean);

    const filteredAttachments =
      attCodes.length > 0
        ? savedAttachments.filter(
            (item) =>
              !(
                attCodes.includes(item.code?.value) &&
                (!item.referenceNumber?.trim() || !item.attachmentDate || !item.fileUrl)
              )
          )
        : savedAttachments;

    if (filteredAttachments.length === savedAttachments.length) {
      append(uploadedAttachments);
    } else {
      remove();
      append([
        ...filteredAttachments.map(({ id, ...existingAttachment }) => ({
          ...existingAttachment,
        })),
        ...uploadedAttachments,
      ]);
    }

    setSavedAttachments([
      ...filteredAttachments,
      ...uploadedAttachments.map((uploadedAttachment) => ({
        ...uploadedAttachment,
        id: crypto.randomUUID(),
      })),
    ]);

    toasterSuccess({ title: 'File upload successful' });
  };

  const handleImportAttachmentsFromParent = (uploadedAttachments: IAttachmentFormValues[]) => {
    const addedAttachmentFormValues = uploadedAttachments.map(({ id, ...rest }) => ({
      ...rest,
      parentAttachmentId: id,
    }));

    append(addedAttachmentFormValues);

    setSavedAttachments((prev) => [
      ...prev,
      ...addedAttachmentFormValues.map((uploadedAttachment) => ({
        ...uploadedAttachment,
        id: crypto.randomUUID(),
      })),
    ]);

    setShowImportFromParentModal(false);
  };

  const handleAddAttachment = () => {
    const addedAttachmentValues = getValues(`${ArrayFields.attachedDocuments}.${selectedAttachmentIndex as number}`);
    setSavedAttachments([
      ...savedAttachments,
      {
        ...addedAttachmentValues,
        id: crypto.randomUUID(),
      },
    ]);
    setSelectedAttachmentIndex(null);
  };

  const handleUpdateAttachment = () => {
    const updatedAttachmentIndex = selectedAttachmentIndex as number;
    const newValues = getValues(`${ArrayFields.attachedDocuments}.${updatedAttachmentIndex}`);
    const newAttachments = [...savedAttachments];

    newAttachments[updatedAttachmentIndex] = {
      id: newAttachments[updatedAttachmentIndex]?.id,
      ...newValues,
    };

    setSavedAttachments(newAttachments);
    setSelectedAttachmentIndex(null);
  };

  const selectAttachment = (index: number) => {
    if (index === selectedAttachmentIndex) {
      return;
    }

    if (
      (permissions?.add || permissions?.modify) &&
      typeof selectedAttachmentIndex === 'number' &&
      getSelectedAttachmentValuesAreChanged()
    ) {
      setNextSelectedAttachmentIndex(index);

      return;
    }

    setSelectedAttachmentIndex(index);
  };

  const discardChangesAndContinue = () => {
    handleCancel();
    setSelectedAttachmentIndex(nextSelectedAttachmentIndex);
    setNextSelectedAttachmentIndex(null);

    if (nextSelectedAttachmentIndex === savedAttachments.length) {
      openCreateForm();
    }
  };

  return (
    <>
      <ListWithContentLayout
        sidebar={
          <AttachmentList
            openImportFromParentModal={() => setShowImportFromParentModal(true)}
            selectedAttachmentIndex={selectedAttachmentIndex}
            onUploadAttachments={handleUploadAttachments}
            selectAttachment={selectAttachment}
            attachments={savedAttachments}
          />
        }
      >
        {typeof selectedAttachmentIndex === 'number' ? (
          <AttachmentForm
            onSave={formType === 'create' ? handleAddAttachment : handleUpdateAttachment}
            onDelete={() => setShowDeletePopup(true)}
            index={selectedAttachmentIndex}
            key={selectedAttachmentIndex}
            onCancel={handleCancel}
            type={formType}
          />
        ) : (
          <EmptyContentView
            button={
              <UploadAttachmentsButton
                button={
                  // @ts-expect-error onClick prop will be passed via cloneElement in UploadAttachmentsButtonComponent
                  <AttachNewDocumentButton onImport={() => setShowImportFromParentModal(true)} className="m-t-12" />
                }
                onUploadAttachments={handleUploadAttachments}
              />
            }
            showCreateButton={!viewMode && !!permissions?.add}
            showMessage={!savedAttachments.length}
            onCreateItem={openCreateForm}
            text="There is no document"
          />
        )}
      </ListWithContentLayout>
      {showDeletePopup && (
        <ConfirmationModal
          onCancel={() => setShowDeletePopup(false)}
          type={ConfirmationModalType.Deletion}
          onAccept={handleDeleteAttachment}
          title={t('Delete Document')}
          cancelText={t('Cancel')}
          acceptText={t('Delete')}
          id="confirmation-modal"
          isOpen
        >
          {t('Are you sure you want to delete Document?')}
        </ConfirmationModal>
      )}
      {typeof nextSelectedAttachmentIndex === 'number' && (
        <ConfirmationModal
          onCancel={() => setNextSelectedAttachmentIndex(null)}
          type={ConfirmationModalType.Deletion}
          onAccept={discardChangesAndContinue}
          title={t('Confirmation')}
          cancelText={t('Cancel')}
          id="confirmation-modal"
          acceptText={t('Yes')}
          isOpen
        >
          {t('Unsaved information will be deleted. Are you sure you want to proceed?')}
        </ConfirmationModal>
      )}
      {showImportFromParentModal && (
        <AttachmentBulkUploadModal
          onCancel={() => setShowImportFromParentModal(false)}
          uploadedAttachments={parentLpcoAttachmentValues}
          onConfirm={handleImportAttachmentsFromParent}
          title="Import Documents from Parent LPCO"
          mode="import"
        />
      )}
    </>
  );
}

export default Attachments;
